import { useCreate, useRedirect } from "ra-core";
import { Link } from "react-router";
import { useState } from "react";
import {
  ArrowLeft,
  User,
  Phone,
  Mail,
  Users,
  Save,
  Plus,
  Briefcase,
  Trash2,
  MapPin,
  DollarSign,
} from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { CONTACT_SOURCES, JOB_TYPES } from "../constants";
import { AddressInput } from "../AddressInput";

interface PendingJob {
  id: string;
  address: string;
  job_type: string;
  estimated_value: number;
}

export const ContactCreate = () => {
  const [create, { isPending: isCreating }] = useCreate();
  const redirect = useRedirect();

  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [email, setEmail] = useState("");
  const [address, setAddress] = useState("");
  const [source, setSource] = useState<string>("referral");

  // Pending jobs to create after contact is created
  const [pendingJobs, setPendingJobs] = useState<PendingJob[]>([]);
  const [showJobForm, setShowJobForm] = useState(false);
  const [newJobAddress, setNewJobAddress] = useState("");
  const [newJobType, setNewJobType] = useState("roof_replacement");
  const [newJobValue, setNewJobValue] = useState("");

  const addPendingJob = () => {
    if (!newJobAddress.trim()) return;

    setPendingJobs([
      ...pendingJobs,
      {
        id: crypto.randomUUID(),
        address: newJobAddress.trim(),
        job_type: newJobType,
        estimated_value: Number(newJobValue) || 0,
      },
    ]);

    // Reset form
    setNewJobAddress("");
    setNewJobType("roof_replacement");
    setNewJobValue("");
    setShowJobForm(false);
  };

  const removePendingJob = (id: string) => {
    setPendingJobs(pendingJobs.filter((job) => job.id !== id));
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat("en-US", {
      style: "currency",
      currency: "USD",
      maximumFractionDigits: 0,
    }).format(value);

  const handleSubmit = async () => {
    if (!name.trim()) {
      alert("Please enter a name");
      return;
    }

    // Create contact first
    create(
      "contacts",
      {
        data: {
          name: name.trim(),
          phone: phone.trim(),
          email: email.trim(),
          address: address.trim(),
          source,
          job_count: pendingJobs.length,
          total_value: pendingJobs.reduce(
            (sum, job) => sum + job.estimated_value,
            0,
          ),
        },
      },
      {
        onSuccess: async (contactData) => {
          // Create all pending jobs with the new contact ID
          for (const job of pendingJobs) {
            await create("jobs", {
              data: {
                contact_id: contactData.id,
                address: job.address,
                job_type: job.job_type,
                estimated_value: job.estimated_value,
                roof_sqft: 0,
                status: "new_lead",
                notes: "",
                index: 0,
              },
            });
          }
          redirect("show", "contacts", contactData.id);
        },
      },
    );
  };

  return (
    <div className="p-4 max-w-2xl mx-auto">
      {/* Back button */}
      <Link
        to="/contacts"
        className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-4"
      >
        <ArrowLeft className="h-4 w-4" />
        Back to Contacts
      </Link>

      {/* Header */}
      <h1 className="text-2xl font-semibold mb-6 flex items-center gap-2">
        <User className="h-6 w-6" />
        Create New Contact
      </h1>

      {/* Form Card */}
      <Card>
        <CardHeader>
          <CardTitle>Contact Details</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Name */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <User className="h-4 w-4" />
              Name <span className="required-asterisk">*</span>
            </Label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="John Doe"
            />
          </div>

          {/* Phone */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Phone className="h-4 w-4" />
              Phone
            </Label>
            <Input
              type="tel"
              value={phone}
              onChange={(e) => setPhone(e.target.value)}
              placeholder="(555) 123-4567"
            />
          </div>

          {/* Email */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Mail className="h-4 w-4" />
              Email
            </Label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="john@example.com"
            />
          </div>

          {/* Address */}
          <div className="space-y-2">
            <Label>Address</Label>
            <AddressInput
              value={address}
              onChange={setAddress}
              placeholder="123 Main St, City, State 12345"
            />
          </div>

          {/* Source */}
          <div className="space-y-2">
            <Label className="flex items-center gap-2">
              <Users className="h-4 w-4" />
              Source
            </Label>
            <Select value={source} onValueChange={setSource}>
              <SelectTrigger className="w-48">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {CONTACT_SOURCES.map((src) => (
                  <SelectItem key={src.id} value={src.id}>
                    <span className="flex items-center gap-2">
                      <src.icon className={`h-4 w-4 ${src.color}`} />
                      {src.label}
                    </span>
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4">
            <Button
              onClick={handleSubmit}
              disabled={isCreating || !name.trim()}
            >
              <Save className="h-4 w-4 mr-2" />
              {isCreating ? "Creating..." : "Create Contact"}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Jobs Card */}
      <Card className="mt-6">
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <Briefcase className="h-5 w-5" />
              Jobs
            </CardTitle>
            {!showJobForm && (
              <Button
                variant="outline"
                size="sm"
                onClick={() => setShowJobForm(true)}
              >
                <Plus className="h-4 w-4 mr-1" />
                Add Job
              </Button>
            )}
          </div>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Add Job Form */}
          {showJobForm && (
            <div className="border rounded-lg p-4 space-y-4 bg-muted/30">
              <div className="space-y-2">
                <Label className="flex items-center gap-2">
                  <MapPin className="h-4 w-4" />
                  Address <span className="required-asterisk">*</span>
                </Label>
                <AddressInput
                  value={newJobAddress}
                  onChange={setNewJobAddress}
                  placeholder="123 Main St, City, State 12345"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <Briefcase className="h-4 w-4" />
                    Job Type
                  </Label>
                  <Select value={newJobType} onValueChange={setNewJobType}>
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {JOB_TYPES.map((type) => (
                        <SelectItem key={type.id} value={type.id}>
                          <span className="flex items-center gap-2">
                            <type.icon className={`h-4 w-4 ${type.color}`} />
                            {type.label}
                          </span>
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div className="space-y-2">
                  <Label className="flex items-center gap-2">
                    <DollarSign className="h-4 w-4" />
                    Estimated Value
                  </Label>
                  <Input
                    type="number"
                    value={newJobValue}
                    onChange={(e) => setNewJobValue(e.target.value)}
                    placeholder="15000"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setShowJobForm(false);
                    setNewJobAddress("");
                    setNewJobType("roof_replacement");
                    setNewJobValue("");
                  }}
                >
                  Cancel
                </Button>
                <Button
                  size="sm"
                  onClick={addPendingJob}
                  disabled={!newJobAddress.trim()}
                >
                  <Plus className="h-4 w-4 mr-1" />
                  Add Job
                </Button>
              </div>
            </div>
          )}

          {/* Pending Jobs List */}
          {pendingJobs.length > 0 ? (
            <div className="space-y-2">
              {pendingJobs.map((job) => {
                const jobType = JOB_TYPES.find((t) => t.id === job.job_type);
                return (
                  <div
                    key={job.id}
                    className="flex items-center justify-between p-3 border rounded-lg"
                  >
                    <div className="flex items-center gap-3">
                      {jobType && (
                        <jobType.icon className={`h-5 w-5 ${jobType.color}`} />
                      )}
                      <div>
                        <div className="font-medium">{job.address}</div>
                        <div className="text-sm text-muted-foreground">
                          {jobType?.label || job.job_type}
                          {job.estimated_value > 0 && (
                            <> • {formatCurrency(job.estimated_value)}</>
                          )}
                        </div>
                      </div>
                    </div>
                    <Button
                      variant="ghost"
                      size="icon"
                      onClick={() => removePendingJob(job.id)}
                    >
                      <Trash2 className="h-4 w-4 text-red-500" />
                    </Button>
                  </div>
                );
              })}
            </div>
          ) : (
            !showJobForm && (
              <p className="text-sm text-muted-foreground text-center py-4">
                No jobs added yet. Click "Add Job" to create jobs for this
                contact.
              </p>
            )
          )}
        </CardContent>
      </Card>
    </div>
  );
};
